import { useState, useEffect } from 'react';
import Cal, { getCalApi } from '@calcom/embed-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertCircle, ArrowLeft, Calendar, Loader2 } from 'lucide-react';
import { billingApi, type CancellationReason } from '@/lib/api';

const CONFIRM_PHRASE = 'CANCEL NOW';
const FEEDBACK_MIN_CHARS = 25;

// Mirrors Stripe's cancellation_details.feedback enum so reasons are queryable in Stripe.
const CANCELLATION_REASONS: { value: CancellationReason; label: string }[] = [
  { value: 'unused', label: "I'm not using it enough" },
  { value: 'too_expensive', label: 'It costs too much' },
  { value: 'low_quality', label: "Something isn't working right" },
  { value: 'missing_features', label: "It's missing something I need" },
  { value: 'too_complex', label: "It's too complicated" },
  { value: 'customer_service', label: 'I had a bad support experience' },
  { value: 'switched_service', label: "I'm switching to another tool" },
  { value: 'other', label: 'Something else' },
];

type Step = 'help' | 'confirm' | 'feedback';

interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancelled: () => void;
}

export function CancelSubscriptionDialog({
  open,
  onOpenChange,
  onCancelled,
}: CancelSubscriptionDialogProps) {
  const [step, setStep] = useState<Step>('help');
  const [confirmText, setConfirmText] = useState('');
  const [feedback, setFeedback] = useState('');
  const [reason, setReason] = useState<CancellationReason | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    (async () => {
      const cal = await getCalApi({ namespace: 'invoiceowl' });
      cal('ui', { theme: 'light', hideEventTypeDetails: false, layout: 'month_view' });
      cal('on', {
        action: 'bookingSuccessful',
        callback: () => onOpenChange(false),
      });
    })();
  }, [open, onOpenChange]);

  // Reset state whenever the dialog reopens
  useEffect(() => {
    if (open) {
      setStep('help');
      setConfirmText('');
      setFeedback('');
      setReason('');
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);
    setError('');
    const response = await billingApi.cancelSubscription(feedback.trim(), reason);
    if (response.error) {
      setError(response.error);
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onCancelled();
    onOpenChange(false);
  };

  const confirmMatches = confirmText.trim() === CONFIRM_PHRASE;
  const feedbackValid = feedback.trim().length >= FEEDBACK_MIN_CHARS && reason !== '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          step === 'help'
            ? 'sm:max-w-5xl w-[95vw] max-h-[95vh] overflow-y-auto'
            : 'max-h-[90vh] overflow-y-auto'
        }
      >
        {step === 'help' && (
          <>
            <DialogHeader>
              <DialogTitle>Before you cancel — talk to the founder</DialogTitle>
              <DialogDescription>
                If something isn't working, I'd love 15 minutes to fix it. Pick a time
                that works for you, or skip ahead if you're sure.
              </DialogDescription>
            </DialogHeader>

            <div className="border rounded-lg overflow-hidden bg-muted/20 h-[55vh] min-h-[440px] max-h-[560px]">
              <Cal
                namespace="invoiceowl"
                calLink="zallarak/invoiceowl"
                style={{ width: '100%', height: '100%', overflow: 'scroll' }}
                config={{ layout: 'month_view', theme: 'light' }}
              />
            </div>

            <DialogFooter className="sm:justify-between gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Keep my subscription
              </Button>
              <Button variant="secondary" onClick={() => setStep('confirm')}>
                Continue to cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle>Confirm cancellation</DialogTitle>
              <DialogDescription>
                You'll keep access until the end of your current billing period. To
                continue, type{' '}
                <span className="font-mono font-semibold text-foreground">
                  {CONFIRM_PHRASE}
                </span>{' '}
                below.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-2">
              <Label htmlFor="cancel-confirm-input">Type {CONFIRM_PHRASE} to confirm</Label>
              <Input
                id="cancel-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                autoFocus
                autoComplete="off"
                className="font-mono"
              />
            </div>

            <DialogFooter className="sm:justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep('help')}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                variant="secondary"
                onClick={() => setStep('feedback')}
                disabled={!confirmMatches}
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'feedback' && (
          <>
            <DialogHeader>
              <DialogTitle>One last thing — why are you cancelling?</DialogTitle>
              <DialogDescription>
                Your feedback goes straight to the founders. We read every word and use
                it to make InvoiceOwl better.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <Label>What's the main reason?</Label>
              <RadioGroup
                value={reason}
                onValueChange={(value) => setReason(value as CancellationReason)}
                className="grid gap-1.5"
                disabled={submitting}
              >
                {CANCELLATION_REASONS.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`cancel-reason-${option.value}`}
                    className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5 text-sm cursor-pointer hover:bg-muted/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                  >
                    <RadioGroupItem value={option.value} id={`cancel-reason-${option.value}`} />
                    <span>{option.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2 py-2">
              <Label htmlFor="cancel-feedback">
                Tell us more ({FEEDBACK_MIN_CHARS} characters minimum)
              </Label>
              <Textarea
                id="cancel-feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="What's missing, broken, or making this hard for you?"
                rows={5}
                autoFocus
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                {feedback.trim().length} / {FEEDBACK_MIN_CHARS}
              </p>
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 text-destructive rounded-md flex items-start gap-2 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <DialogFooter className="sm:justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep('confirm')} disabled={submitting}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                variant="secondary"
                onClick={handleSubmit}
                disabled={!feedbackValid || submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <Calendar className="h-4 w-4" />
                    Cancel my subscription
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
